import authService from './authService';
import { OrderType, OrderTypeFormData, OrderTypeSearchModel } from '../types/orderType';
import { PaginatedResponse, ApiResponse } from '../types/common';

class OrderTypeService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  async getAll(searchModel: OrderTypeSearchModel): Promise<PaginatedResponse<OrderType>> {
    const params = new URLSearchParams({
      page: searchModel.page.toString(),
      page_size: searchModel.pageSize.toString(),
      ...(searchModel.searchName && { q: searchModel.searchName }),
    });

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/order-types?${params}`);

    if (!response.ok) {
      throw new Error('Falha ao buscar tipos de pedido');
    }

    const json = await response.json();
    const items = Array.isArray(json.data) ? json.data : [];
    const pg = json.pagination ?? {};

    return {
      success: json.success ?? true,
      data: items,
      pagination: {
        page: pg.page ?? searchModel.page,
        limit: pg.page_size ?? searchModel.pageSize,
        total: pg.total ?? items.length,
        totalPages: pg.total_pages ?? 1,
      },
    };
  }

  async getById(id: number): Promise<OrderType> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/order-types/${id}`);

    if (!response.ok) {
      if (response.status === 404) throw new Error('Tipo de pedido não encontrado');
      throw new Error('Falha ao buscar tipo de pedido');
    }

    const data: ApiResponse<OrderType> = await response.json();
    return data.data!;
  }

  async create(formData: OrderTypeFormData): Promise<OrderType> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/order-types', formData);

    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao criar tipo de pedido');
    }

    const data: ApiResponse<OrderType> = await response.json();
    return data.data!;
  }

  async update(id: number, formData: OrderTypeFormData): Promise<OrderType> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/order-types/${id}`, formData);

    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao atualizar tipo de pedido');
    }

    const data: ApiResponse<OrderType> = await response.json();
    return data.data!;
  }

  async delete(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/order-types/${id}`);

    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao excluir tipo de pedido');
    }
  }

  /** Returns all order types (unpaginated) for use in selects/dropdowns */
  async listAll(): Promise<OrderType[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/order-types?page=1&page_size=100');

    if (!response.ok) {
      throw new Error('Falha ao buscar tipos de pedido');
    }

    const json = await response.json();
    return Array.isArray(json.data) ? json.data : [];
  }
}

export const orderTypeService = new OrderTypeService();
export default orderTypeService;
